/**
 * Campaign roster + invite panel (#263).
 *
 * Displayed on the Settings → General tab (GM-only section). Shows:
 *   - Full member roster (including the implicit owner with role='owner')
 *   - Remove member buttons (owner/GM)
 *   - Invite-by-email form (owner/GM; GM-role invite requires implicit_gm)
 *   - Shareable link creation (owner/GM)
 *   - Pending invite list with revoke button
 *   - Active link list with close button
 *
 * llui signal-runtime constraints:
 *   - No `.peek()` in reactive slots — only in event handlers.
 *   - `state` reads via `.at('field')` or `.map(fn)`.
 *   - Inside `each`, combine state with the row via `derived([state, item], ...)`.
 *   - No Maps/Sets/Dates/class instances in rendered data.
 *   - All strings through `t()`.
 */

import { card } from '@dl/ui';
import {
  button,
  derived,
  div,
  each,
  h2,
  input,
  label,
  option,
  p,
  select,
  show,
  span,
  text,
  ul,
  li,
  type Mountable,
  type Signal,
} from '@llui/dom';

import { t } from './i18n.js';

/**
 * Minimal Model slice the view needs. A generic so the host's wider
 * `Model` satisfies it structurally without importing the full type.
 */
export interface CampaignMembersSlice {
  campaignMembers: readonly {
    user_id: string;
    email: string;
    role: string;
    is_owner?: boolean;
  }[] | null;
  inviteState: {
    emailInvites: readonly { id: string; invited_email: string; role: string; expires_at: string }[];
    links: readonly {
      id: string;
      role: string;
      created_at: string;
      expires_at: string | null;
      use_count: number;
    }[];
  } | null;
  inviteEmailForm: { email: string; role: 'gm' | 'player'; sending: boolean } | null;
  /**
   * #264 — Per-member controlled Character assignments derived from the
   * local replica's entity facts. Keyed by userId, value is a list of
   * `{ id, name }` for each Character the member controls. Computed in
   * app.ts from `entities` so no extra API call is needed — the data is
   * already in the local replica.
   *
   * `null` means the entities haven't loaded yet (pre-substrate).
   */
  memberControlledCharacters: Record<string, readonly { id: string; name: string }[]> | null;
}

/**
 * Message subset the panel dispatches. The host's wider `Msg` must include
 * all of these (structural compatibility).
 */
export type CampaignMembersMsg =
  | { type: 'roster/invites-loaded'; emailInvites: readonly { id: string; invited_email: string; role: string; expires_at: string }[]; links: readonly { id: string; role: string; created_at: string; expires_at: string | null; use_count: number }[] }
  | { type: 'roster/invite-email-change'; email: string }
  | { type: 'roster/invite-email-role'; role: 'gm' | 'player' }
  | { type: 'roster/invite-email-send' }
  | { type: 'roster/invite-email-sent' }
  | { type: 'roster/invite-revoke'; inviteId: string }
  | { type: 'roster/link-create'; role: 'gm' | 'player' }
  | { type: 'roster/link-close'; linkId: string }
  | { type: 'roster/member-remove'; userId: string }
  | { type: 'roster/load-invites' };

function roleLabel(role: string): string {
  if (role === 'owner') return t('settings.roster.role.owner');
  if (role === 'gm') return t('settings.roster.role.gm');
  return t('settings.roster.role.player');
}

/**
 * Campaign roster + invite management panel.
 *
 * @param state Signal<CampaignMembersSlice> — the host passes a derived
 *   or mapped signal of the minimal slice so this view is decoupled from
 *   the full Model. Must NOT be `state.peek()`'d in the render body (peek-in-slot lint).
 * @param send Send<Msg> — the host's send function (structural duck-typing).
 * @param isOwner Whether the current user is the account owner. Passed as
 *   a literal from the host's `branch` arm so no render-body `.peek()` is needed.
 */
export function viewCampaignMembers(
  state: Signal<CampaignMembersSlice>,
  send: (msg: CampaignMembersMsg) => void,
  isOwner: boolean,
): Mountable {
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-roster',
    children: [
      h2({ class: 'settings-label' }, [text(t('settings.roster.heading'))]),
      p({ class: 'settings-help' }, [text(t('settings.roster.help'))]),

      // ── Current roster ──────────────────────────────────────────────
      div({ class: 'roster-member-list', 'data-testid': 'roster-member-list' }, [
        show(
          state.map((s) => (s.campaignMembers?.length ?? 0) === 0),
          () => [
            p({ class: 'roster-empty', 'data-testid': 'roster-empty' }, [
              text(t('settings.roster.empty')),
            ]),
          ],
        ),
        each(
          state.map((s) => (s.campaignMembers ?? []).map((m) => ({ ...m }))),
          {
            key: (m) => m.user_id,
            render: (item) => {
              const row = derived([state, item], (_s, m) => m);
              // Per-member Character list: join the slice's `memberControlledCharacters`
              // map with this row's userId. Derived so the signal tracks correctly.
              const chars = derived([state, item], (s, m) =>
                (s.memberControlledCharacters?.[m.user_id] ?? []).map((c) => ({ ...c })),
              );
              return [
                div({ class: 'roster-member', 'data-testid': 'roster-member' }, [
                  span({ class: 'roster-member-email' }, [text(row.map((r) => r.email))]),
                  span({ class: 'roster-member-role' }, [text(row.map((r) => roleLabel(r.role)))]),
                  // #264 — Assigned Characters (informational; assignment is done via
                  // the controller picker on the Character entity detail page).
                  div({ class: 'roster-member-characters', 'data-testid': 'roster-member-characters' }, [
                    span({ class: 'roster-member-characters-label' }, [
                      text(t('settings.roster.characters.label')),
                    ]),
                    show(
                      chars.map((cs) => cs.length === 0),
                      () => [
                        span({ class: 'roster-member-characters-none' }, [
                          text(t('settings.roster.characters.none')),
                        ]),
                      ],
                    ),
                    show(
                      chars.map((cs) => cs.length > 0),
                      () => [
                        ul({ class: 'roster-member-characters-list' }, [
                          each(chars, {
                            key: (c) => c.id,
                            render: (charItem) => {
                              const charRow = derived([chars, charItem], (_cs, c) => c);
                              return [
                                li({ class: 'roster-member-characters-item', 'data-testid': 'roster-member-character' }, [
                                  span({ class: 'roster-member-characters-name' }, [
                                    text(charRow.map((c) => c.name)),
                                  ]),
                                ]),
                              ];
                            },
                          }),
                        ]),
                      ],
                    ),
                  ]),
                  // Remove button — not shown for the owner row.
                  show(
                    row.map((r) => !r.is_owner),
                    () => [
                      button({
                        class: 'roster-member-remove',
                        'data-testid': 'roster-member-remove',
                        onClick: () => {
                          const userId = row.peek().user_id;
                          send({ type: 'roster/member-remove', userId });
                        },
                      }, [text(t('settings.roster.remove'))]),
                    ],
                  ),
                ]),
              ];
            },
          },
        ),
      ]),

      // ── Invite by email ──────────────────────────────────────────────
      div({ class: 'roster-invite-email', 'data-testid': 'roster-invite-email' }, [
        h2({ class: 'roster-section-heading' }, [text(t('settings.roster.invite_email.heading'))]),
        div({ class: 'roster-invite-email-form' }, [
          label({ for: 'roster-invite-email-input', class: 'roster-form-label' }, [
            text(t('settings.roster.invite_email.label')),
          ]),
          input({
            id: 'roster-invite-email-input',
            type: 'email',
            class: 'roster-form-input',
            'data-testid': 'roster-invite-email-input',
            placeholder: t('settings.roster.invite_email.placeholder'),
            value: state.map((s) => s.inviteEmailForm?.email ?? ''),
            onInput: (e) => {
              const val = (e.target as HTMLInputElement).value;
              send({ type: 'roster/invite-email-change', email: val });
            },
          }),
          label({ for: 'roster-invite-role-select', class: 'roster-form-label' }, [
            text(t('settings.roster.invite_email.role_label')),
          ]),
          select({
            id: 'roster-invite-role-select',
            class: 'roster-form-select',
            'data-testid': 'roster-invite-role-select',
            onChange: (e) => {
              const val = (e.target as HTMLSelectElement).value as 'gm' | 'player';
              send({ type: 'roster/invite-email-role', role: val });
            },
          }, [
            option({ value: 'player' }, [text(t('settings.roster.role.player'))]),
            // Only the account owner can create GM invites.
            ...(isOwner
              ? [option({ value: 'gm' }, [text(t('settings.roster.role.gm'))])]
              : []),
          ]),
          button({
            class: 'roster-invite-send',
            'data-testid': 'roster-invite-send',
            disabled: state.map((s) => !s.inviteEmailForm?.email || s.inviteEmailForm.sending),
            onClick: () => {
              send({ type: 'roster/invite-email-send' });
            },
          }, [
            text(
              state.map((s) =>
                s.inviteEmailForm?.sending
                  ? t('settings.roster.invite_email.sending')
                  : t('settings.roster.invite_email.send'),
              ),
            ),
          ]),
        ]),
      ]),

      // ── Shareable links ──────────────────────────────────────────────
      div({ class: 'roster-invite-links', 'data-testid': 'roster-invite-links' }, [
        h2({ class: 'roster-section-heading' }, [text(t('settings.roster.invite_link.heading'))]),
        button({
          class: 'roster-link-create',
          'data-testid': 'roster-link-create-player',
          onClick: () => send({ type: 'roster/link-create', role: 'player' }),
        }, [text(t('settings.roster.invite_link.create'))]),
        ...(isOwner
          ? [
              button({
                class: 'roster-link-create roster-link-create-gm',
                'data-testid': 'roster-link-create-gm',
                onClick: () => send({ type: 'roster/link-create', role: 'gm' }),
              }, [text(t('settings.roster.invite_link.create_gm'))]),
            ]
          : []),

        // Active links.
        h2({ class: 'roster-section-heading' }, [text(t('settings.roster.active_links.heading'))]),
        show(
          state.map((s) => (s.inviteState?.links.length ?? 0) === 0),
          () => [
            p({ class: 'roster-empty' }, [text(t('settings.roster.active_links.empty'))]),
          ],
        ),
        each(
          state.map((s) => (s.inviteState?.links ?? []).map((l) => ({ ...l }))),
          {
            key: (l) => l.id,
            render: (item) => {
              const row = derived([state, item], (_s, l) => l);
              return [
                div({ class: 'roster-link', 'data-testid': 'roster-link' }, [
                  span({ class: 'roster-link-role' }, [text(row.map((r) => roleLabel(r.role)))]),
                  span({ class: 'roster-link-uses' }, [
                    text(row.map((r) => t('settings.roster.invite_link.uses', { count: r.use_count }))),
                  ]),
                  button({
                    class: 'roster-link-close',
                    'data-testid': 'roster-link-close',
                    onClick: () => {
                      const linkId = row.peek().id;
                      send({ type: 'roster/link-close', linkId });
                    },
                  }, [text(t('settings.roster.invite_link.close'))]),
                ]),
              ];
            },
          },
        ),
      ]),

      // ── Pending email invites ─────────────────────────────────────────
      div({ class: 'roster-pending-invites', 'data-testid': 'roster-pending-invites' }, [
        h2({ class: 'roster-section-heading' }, [text(t('settings.roster.pending_invites.heading'))]),
        show(
          state.map((s) => (s.inviteState?.emailInvites.length ?? 0) === 0),
          () => [
            p({ class: 'roster-empty' }, [text(t('settings.roster.pending_invites.empty'))]),
          ],
        ),
        each(
          state.map((s) => (s.inviteState?.emailInvites ?? []).map((inv) => ({ ...inv }))),
          {
            key: (inv) => inv.id,
            render: (item) => {
              const row = derived([state, item], (_s, inv) => inv);
              return [
                div({ class: 'roster-invite', 'data-testid': 'roster-invite' }, [
                  span({ class: 'roster-invite-email' }, [text(row.map((r) => r.invited_email))]),
                  span({ class: 'roster-invite-role' }, [text(row.map((r) => roleLabel(r.role)))]),
                  button({
                    class: 'roster-invite-revoke',
                    'data-testid': 'roster-invite-revoke',
                    onClick: () => {
                      const inviteId = row.peek().id;
                      send({ type: 'roster/invite-revoke', inviteId });
                    },
                  }, [text(t('settings.roster.pending_invites.revoke'))]),
                ]),
              ];
            },
          },
        ),
      ]),
    ],
  });
}


